import { Injectable } from '@angular/core';
import { OverviewMaterialDsService, SecondaryMaterialDsService, BillOfMaterialDsService } from '@app/core/data-services';
import { MaterialTableTreeNode } from '@app/modules/mounting/models/material-table.model';
import { ActiveWorkCenterService } from '@app/core/workcenter';
import { BehaviorSubject, Observable, ReplaySubject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { ScanContainerResponse, SplitBillOfMaterialData, SplitBillOfMaterialRowEntry } from 'chronos-core-client';

@Injectable()
export class TabSecondaryService {
  private secondaryMaterialSubject = new ReplaySubject<MaterialTableTreeNode[]>(1);
  private secondaryMountedMaterialSubject = new ReplaySubject<MaterialTableTreeNode[]>(1);
  private isSecondaryMaterialLoadingSubject = new BehaviorSubject<boolean>(false);
  private isSecondaryMountedMaterialLoadingSubject = new BehaviorSubject<boolean>(false);

  public secondaryMaterial$: Observable<MaterialTableTreeNode[]> = this.secondaryMaterialSubject.asObservable();
  public isSecondaryMaterialLoading$: Observable<boolean> = this.isSecondaryMaterialLoadingSubject.asObservable();
  public secondaryMountedMaterial$: Observable<MaterialTableTreeNode[]> = this.secondaryMountedMaterialSubject.asObservable();
  public isSecondaryMountedMaterialLoading$: Observable<boolean> = this.isSecondaryMountedMaterialLoadingSubject.asObservable();

  constructor(
    private overviewMaterialDsService: OverviewMaterialDsService,
    private secondaryMaterialDsService: SecondaryMaterialDsService,
    private activeWorkCenterService: ActiveWorkCenterService,
    private billOfMaterialDsService: BillOfMaterialDsService
  ) {}

  public getSecondaryMaterialOverviewData(): Observable<MaterialTableTreeNode[]> {
    this.isSecondaryMaterialLoadingSubject.next(true);
    return this.secondaryMaterialDsService.getSecondaryMaterialOverviewData().pipe(
      tap(
        (secondaryMaterial) => {
          this.isSecondaryMaterialLoadingSubject.next(false);
          this.secondaryMaterialSubject.next(secondaryMaterial);
        },
        () => {
          this.isSecondaryMaterialLoadingSubject.next(false);
          this.secondaryMaterialSubject.next([]);
        }
      )
    );
  }

  public getSecondaryMountedMaterialData(): Observable<MaterialTableTreeNode[]> {
    this.isSecondaryMountedMaterialLoadingSubject.next(true);
    return this.secondaryMaterialDsService.getSecondaryMountedMaterialData().pipe(
      tap(
        (secondaryMaterial) => {
          this.isSecondaryMountedMaterialLoadingSubject.next(false);
          this.secondaryMountedMaterialSubject.next(secondaryMaterial);
        },
        () => {
          this.isSecondaryMountedMaterialLoadingSubject.next(false);
          this.secondaryMountedMaterialSubject.next([]);
        }
      )
    );
  }

  public renewSecondaryMaterial(): void {
    this.getSecondaryMaterialOverviewData().subscribe();
    this.getSecondaryMountedMaterialData().subscribe();
  }

  public getScannedSecondaryContainerInfo(identificationCode: string): Observable<ScanContainerResponse> {
    const workCenterId = this.activeWorkCenterService.getWorkCenterId();
    return this.overviewMaterialDsService.scanIdentificationCode(workCenterId, identificationCode);
  }

  public getMaterialList(): Observable<any> {
    return this.secondaryMaterialDsService.getMaterialList();
  }

  public getBillOfMaterialSplitData(productionOrderId: number, billOfMaterialId: number): Observable<SplitBillOfMaterialData> {
    return this.billOfMaterialDsService.getSplitBillOfMaterialDetails(productionOrderId, billOfMaterialId);
  }

  public splitBillOfMaterialRow(entry: SplitBillOfMaterialRowEntry, productionOrderId: number): Observable<void> {
    return this.billOfMaterialDsService.splitBillOfMaterialRow(entry, productionOrderId);
  }
}
